import type { NormalizedClient } from "@/composables/useClientsQuery";

export interface RenewalKpiCounts {
  dueThisMonth: number;
  renewalsNextMonth: number;
  overdueRenewals: number;
  recentlyRenewed: number;
  expiredAccounts: number;
}

export function isSameMonth(dateValue: string, year: number, month: number) {
  if (!dateValue) return false;
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getFullYear() === year && parsed.getMonth() === month;
}

function isBeforeToday(dateValue: string) {
  if (!dateValue) return false;
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return false;

  parsed.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parsed.getTime() < today.getTime();
}

export function isOverdueRenewal(client: NormalizedClient) {
  if (
    ["Renewed", "Expired", "Cancelled", "Paused"].includes(
      client.renewalStatus
    )
  ) {
    return false;
  }

  return isBeforeToday(client.paymentDueDateValue || client.renewalDateValue);
}

export function isRecentlyRenewed(client: NormalizedClient) {
  if (client.renewalStatus !== "Renewed") {
    return false;
  }

  const candidates = [client.renewalStartDateValue, client.renewalDateValue];
  return candidates.some((value) => {
    if (!value) return false;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return false;
    const diff = Date.now() - parsed.getTime();
    return diff >= 0 && diff <= 1000 * 60 * 60 * 24 * 30;
  });
}

export function getRenewalKpiCounts(
  clients: NormalizedClient[]
): RenewalKpiCounts {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
  const nextYear = nextMonthDate.getFullYear();
  const nextMonth = nextMonthDate.getMonth();

  return {
    dueThisMonth: clients.filter((client) =>
      isSameMonth(client.renewalDateValue, currentYear, currentMonth)
    ).length,
    renewalsNextMonth: clients.filter((client) =>
      isSameMonth(client.renewalDateValue, nextYear, nextMonth)
    ).length,
    overdueRenewals: clients.filter(isOverdueRenewal).length,
    recentlyRenewed: clients.filter(isRecentlyRenewed).length,
    expiredAccounts: clients.filter(
      (client) => client.renewalStatus === "Expired"
    ).length,
  };
}
